/**
 * Reed.ai Design System — Card
 *
 * A flexible shell component used as the foundation for:
 *  - Candidate Cards
 *  - Job Description Cards
 *  - NBA (Next Best Action) Cards
 *
 * Source: Figma "Reed.ai - Global components" — 1:31 (Cards section)
 *
 * Design details:
 *  Default — white bg, 1px border #C2C6DB (piccadilly-blue-70), 8px radius, px-[24px] py-[16px]
 *  Hover   — border #081351 (piccadilly-blue), shadow: 0 4px 16px rgba(79,71,191,0.12)
 *  Selected— border-[1.5px] border-[#CF04A9] (carnaby-pink), shadow: 0 4px 16px rgba(192,188,251,0.3)
 *  Active  — slight scale(0.99) transform
 *  Elevated— box-shadow: 0 2px 8px rgba(79,71,191,0.08) + 0 8px 24px rgba(79,71,191,0.06)
 *
 * Usage: wrap any content. For common patterns, see CardHeader, CardBody, CardFooter sub-components.
 */

import React from 'react';
import { cn } from '../../utils/cn';

// ─── Types ────────────────────────────────────────────────────────────────────

export type CardVariant   = 'default' | 'elevated' | 'selected';
export type CardPadding   = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps {
  /** Visual state variant */
  variant?: CardVariant;
  /** Inner padding size */
  padding?: CardPadding;
  /** Whether the card is interactive (shows hover/active styles) */
  interactive?: boolean;
  /** Whether the card is in selected state */
  selected?: boolean;
  className?: string;
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

// ─── Padding map ──────────────────────────────────────────────────────────────

const PADDING_MAP: Record<CardPadding, string> = {
  none: '',
  sm:   'p-3',
  md:   'px-6 py-4',
  lg:   'px-8 py-6',
};

// ─── Card ─────────────────────────────────────────────────────────────────────

/**
 * Reed.ai Card shell
 *
 * @example
 * // Basic card
 * <Card>
 *   <CardHeader title="John Smith" subtitle="Software Engineer" />
 *   <CardBody>Content here</CardBody>
 * </Card>
 *
 * // Selectable interactive card
 * <Card interactive selected={isSelected} onClick={() => setSelected(!isSelected)}>
 *   ...
 * </Card>
 *
 * // Elevated card
 * <Card variant="elevated" padding="lg">...</Card>
 */
export function Card({
  variant = 'default',
  padding = 'md',
  interactive = false,
  selected = false,
  className,
  children,
  onClick,
}: CardProps) {
  const isSelected = selected || variant === 'selected';

  return (
    <div
      className={cn(
        // Base
        'relative bg-white rounded-[8px] border transition-all duration-200',

        // Default border
        !isSelected && 'border-[#C2C6DB]',

        // Selected state
        isSelected && 'border-[1.5px] border-[#CF04A9]',
        isSelected && '[box-shadow:0_4px_16px_rgba(192,188,251,0.3)]',

        // Elevated
        variant === 'elevated' && !isSelected && '[box-shadow:0_2px_8px_rgba(79,71,191,0.08),0_8px_24px_rgba(79,71,191,0.06)]',

        // Interactive states
        interactive && 'cursor-pointer',
        interactive && !isSelected && 'hover:border-[#081351] hover:[box-shadow:0_4px_16px_rgba(79,71,191,0.12)]',
        interactive && 'active:scale-[0.99]',

        // Padding
        PADDING_MAP[padding],

        className,
      )}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>); } : undefined}
    >
      {children}
    </div>
  );
}

// ─── CardHeader ───────────────────────────────────────────────────────────────

export interface CardHeaderProps {
  /** Primary title */
  title?: React.ReactNode;
  /** Secondary subtitle / meta */
  subtitle?: React.ReactNode;
  /** Optional element to render on the right (e.g. badges, action menu) */
  trailing?: React.ReactNode;
  /** Optional leading element (e.g. avatar, icon) */
  leading?: React.ReactNode;
  className?: string;
}

export function CardHeader({ title, subtitle, trailing, leading, className }: CardHeaderProps) {
  return (
    <div className={cn('flex items-start gap-3', className)}>
      {leading && (
        <div className="shrink-0">{leading}</div>
      )}
      <div className="flex-1 min-w-0">
        {title && (
          <div className="font-display font-medium text-h3 text-[#081351] leading-[1.3] truncate">
            {title}
          </div>
        )}
        {subtitle && (
          <div className="font-body font-normal text-body-sm text-[#4B5563] leading-[1.5] mt-0.5">
            {subtitle}
          </div>
        )}
      </div>
      {trailing && (
        <div className="shrink-0 ml-auto">{trailing}</div>
      )}
    </div>
  );
}

// ─── CardBody ─────────────────────────────────────────────────────────────────

export interface CardBodyProps {
  className?: string;
  children?: React.ReactNode;
}

export function CardBody({ className, children }: CardBodyProps) {
  return (
    <div className={cn('mt-3', className)}>
      {children}
    </div>
  );
}

// ─── CardFooter ───────────────────────────────────────────────────────────────

export interface CardFooterProps {
  className?: string;
  children?: React.ReactNode;
}

export function CardFooter({ className, children }: CardFooterProps) {
  return (
    <div className={cn('mt-4 pt-4 border-t border-[#C2C6DB] flex items-center gap-3', className)}>
      {children}
    </div>
  );
}

export default Card;
